/**
 * Basecamp Todos View — Design Prototype v2
 * Finder-style table with column show/hide, sticky header,
 * row context menu (right-click + …), unified due-date cell.
 * By-Project mode kept for grouped hierarchy view.
 *
 * Access: http://flownotes.localhost/?test=basecamp-todos
 *
 * Product: docs/product/bets/2026-03-25-basecamp-todos-table-report-widget.md
 * Related: docs/implementations/2026-03-17-basecamp-phase2-card-reports-api-handoff.md
 * Assignee facet: docs/product/bets/2026-08-17-org-wide-overdue-query-and-todos-assignee-facet.md
 * Note: All / Hop internal / Client chips are work_scope (project name). People facet is assignee.
 */

import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import {
  RefreshCw, ExternalLink, Bookmark, ChevronRight, Calendar, Layers, Search,
  FileText, MoreHorizontal, Copy, Hash,
  Columns3, Check, X, ArrowRight, ArrowUp, ArrowDown,
  Eye, User2, Clock3, FolderOpen, Pencil,
  type LucideIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { testIds } from '@/lib/test-ids';
import { PreviewPanelToggle } from '@/components/integrations/_shared/preview-panel-toggle';
import { EntityContextMenu, type EntityContextMenuEntry } from '@/components/features/entity-context-menu';
import { AddressBar, type BreadcrumbSegment } from '@/components/ui/address-bar';
import { NavigationButtons } from '@/components/ui/navigation-buttons';
import { ListSurfaceStatusBar } from '@/components/ui/list-surface-status-bar';
import { SHELL_COLUMN_FOOTER_CLASS } from '@/lib/shell-chrome';
import { PreviewPaneHeader } from '@/components/features/preview-pane-header';
import { BasecampIcon } from '@/components/icons/basecamp-icon';
import { TodosAssigneeCombobox } from '@/components/integrations/basecamp/todos-assignee-combobox';
import { groupAssigneeNames } from '@/lib/basecamp-assignee';
import {
  QUIET_OVERFLOW_MENU_COLLISION_PADDING,
  QUIET_OVERFLOW_MENU_CONTENT_COMPACT_CLASS,
} from '@/lib/menu-chrome';
import { maaterProtoAppSkinStyle } from '@/components/prototype/maater-proto-shell';
import {
  ProtoAppHeader,
  ProtoIconRail,
  ProtoModePanel,
  ProtoTabRow,
  type ProtoIconRailActive,
} from '@/components/prototype/maater-proto-chrome';
import { useTheme } from '@/providers/theme-provider';
import { SidebarTreeRow } from '@/components/layout/sidebar/SidebarTreeRow';
import { TooltipProvider } from '@/components/ui/tooltip';

// ─── Types ──────────────────────────────────────────────────────────────────

type ViewMode = 'by-date' | 'by-project';
type ScopeFilter = 'all' | 'hop' | 'client';
type StatusFilter = 'all' | 'overdue' | 'due-today';
/** Assignee facet - separate from work_scope (Hop internal / Client project chips). */
type AssigneeFacet = 'all' | 'me' | 'hop' | 'clients' | 'unassigned' | 'person';

interface MockTodo {
  id: string;
  title: string;
  project: string;
  projectAbbr: string;
  projectColor: string;
  todoList: string;
  dueOn: string | null;
  daysOverdue: number;
  isHop: boolean;
  workSpec: 'in-sync' | 'stale' | 'none';
  workSpecPath?: string;
  workSpecModified?: string;
  lastComment: string | null;
  lastCommentAge: string | null;
  bookmarked: boolean;
  assignee?: string;
  /** Basecamp person kind for prototype (maps to person.client flag in production). */
  assigneeKind?: 'hop' | 'client';
  description?: string;
  createdAt?: string;
}

/** Prototype "current user" for Me facet. */
const PROTOTYPE_ME = 'Rowan S.';
const HOP_ASSIGNEE_NAMES = new Set(['Rowan S.', 'Travis S.']);

type MenuAction =
  | 'open-flownotes' | 'open-basecamp' | 'open-project'
  | 'copy-url' | 'copy-id'
  | 'open-workspec' | 'preview-workspec' | 'refresh-todo'
  | 'due-plus-1w' | 'due-plus-2w' | 'due-next-monday' | 'clear-due'
  | 'toggle-bookmark';

type SortColumn = 'project' | 'title' | 'todolist' | 'assignee' | 'workSpec' | 'activity' | 'due';
type SortDir = 'asc' | 'desc';
interface SortState { column: SortColumn; dir: SortDir; }

// ─── Column registry ─────────────────────────────────────────────────────────

interface ColumnDef {
  id: string;
  label: string;
  defaultVisible: boolean;
  width: string;
  canHide: boolean;
}

const COLUMNS: ColumnDef[] = [
  { id: 'todolist', label: 'List', defaultVisible: true, width: '130px', canHide: true },
  { id: 'assignee', label: 'Assignee', defaultVisible: true, width: '110px', canHide: true },
  { id: 'workSpec', label: 'Spec', defaultVisible: true, width: '80px', canHide: true },
  { id: 'activity', label: 'Activity', defaultVisible: true, width: '200px', canHide: true },
  { id: 'due', label: 'Due', defaultVisible: true, width: '130px', canHide: true },
];

const COL_STORAGE_KEY = 'flownotes-todos-columns-v2';

function useColumnVisibility() {
  const [visible, setVisible] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem(COL_STORAGE_KEY);
      if (saved) return new Set(JSON.parse(saved) as string[]);
    } catch { /* use defaults */ }
    return new Set(COLUMNS.filter(c => c.defaultVisible).map(c => c.id));
  });

  const toggle = useCallback((id: string) => {
    setVisible(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      localStorage.setItem(COL_STORAGE_KEY, JSON.stringify([...next]));
      return next;
    });
  }, []);

  const gridTemplate = useMemo(() => {
    const cols = ['2px', '40px', 'minmax(180px, 1fr)'];
    for (const col of COLUMNS) {
      if (visible.has(col.id)) cols.push(col.width);
    }
    cols.push('40px');
    return cols.join(' ');
  }, [visible]);

  const groupedGridTemplate = useMemo(() => {
    const cols = ['minmax(180px, 1fr)'];
    for (const col of COLUMNS) {
      if (visible.has(col.id)) cols.push(col.width);
    }
    cols.push('40px');
    return cols.join(' ');
  }, [visible]);

  return { visible, toggle, gridTemplate, groupedGridTemplate };
}

// ─── Mock data ───────────────────────────────────────────────────────────────

const MOCK: MockTodo[] = [
  { id: '1', title: 'Redesign Events page on GGSC to feature partner orgs', project: 'Greater Good', projectAbbr: 'GG', projectColor: '#16a34a', todoList: 'General GGSC', dueOn: '2026-03-16', daysOverdue: 2, isHop: false, workSpec: 'in-sync', workSpecPath: 'clients/greater-good/ggsc/todos/9669131017-restructure-events-page/README.md', workSpecModified: '2026-03-14', lastComment: 'Lauren left feedback on the nav', lastCommentAge: '2d ago', bookmarked: true, assignee: 'Rowan S.', assigneeKind: 'hop', description: 'Redesign the Events page to feature partner orgs more prominently. Include filtering by type (workshop, conference, online) and date-based sorting. Partner logos should be visible.', createdAt: '2026-02-28' },
  { id: '2', title: 'Develop a technical specification for improved Fastly/CDN delivery', project: '~Hop Business', projectAbbr: 'HB', projectColor: '#7c3aed', todoList: 'Tasks: Rowan', dueOn: '2026-02-11', daysOverdue: 35, isHop: true, workSpec: 'stale', workSpecPath: 'hop-studios/clients/greater-good/greatergood/todos/8033564231-investigate-cloudflare-viability/README.md', workSpecModified: '2026-01-20', lastComment: 'Hop Suggestion (Rowan) Afforded...', lastCommentAge: '35d ago', bookmarked: false, assignee: 'Rowan S.', assigneeKind: 'hop', description: 'Write a technical spec for switching CDN delivery from current provider to Fastly. Include performance benchmarks, migration plan, and cost analysis.', createdAt: '2026-01-15' },
  { id: '3', title: 'Research AI chatbot options, propose 2-3 options for client review', project: '~Hop Business', projectAbbr: 'HB', projectColor: '#7c3aed', todoList: 'Tasks: Rowan', dueOn: '2026-03-18', daysOverdue: 0, isHop: true, workSpec: 'in-sync', workSpecPath: 'hop-studios/internal/todos/ai-chatbot-research/README.md', workSpecModified: '2026-03-17', lastComment: 'Hop Suggestion (Rowan) Afforded...', lastCommentAge: '1d ago', bookmarked: false, assignee: 'Rowan S.', assigneeKind: 'hop', description: 'Research and evaluate AI chatbot solutions. Consider pricing, customization capabilities, API access, and client suitability. Prepare a comparison document.', createdAt: '2026-03-01' },
  { id: '4', title: 'Identify a better way to flag beta plugins for review', project: '~Hop Business', projectAbbr: 'HB', projectColor: '#7c3aed', todoList: 'Tasks: Rowan', dueOn: '2026-04-07', daysOverdue: -20, isHop: true, workSpec: 'none', lastComment: null, lastCommentAge: null, bookmarked: false, assignee: 'Rowan S.', assigneeKind: 'hop', description: 'Current WordPress plugin review process lacks visibility into beta/experimental plugins. Need a system to flag them before auto-update.', createdAt: '2026-03-10' },
  { id: '5', title: 'Set up Uptime monitoring once the new site/app is launched', project: 'LAFA', projectAbbr: 'LA', projectColor: '#0284c7', todoList: 'Technical & Development', dueOn: '2025-10-06', daysOverdue: 163, isHop: false, workSpec: 'none', lastComment: 'Let client know that this was done', lastCommentAge: '5mo ago', bookmarked: false, assignee: 'Chris B.', assigneeKind: 'client', description: 'Configure uptime monitoring for the LAFA site post-launch. Use existing UptimeRobot account. Set up alerts for the team.', createdAt: '2025-09-15' },
  { id: '6', title: 'Set-up Docusign live API & API integration', project: 'LAFA', projectAbbr: 'LA', projectColor: '#0284c7', todoList: 'Technical & Development', dueOn: '2026-04-21', daysOverdue: -34, isHop: false, workSpec: 'in-sync', workSpecPath: 'hop-studios/clients/lafa/todos/docusign-api-integration/README.md', workSpecModified: '2026-03-12', lastComment: 'Hey Ash, Please proceed with setting up...', lastCommentAge: '3d ago', bookmarked: true, assignee: 'Ash K.', assigneeKind: 'client', description: 'Switch from Docusign sandbox to live API. Update all OAuth credentials, test envelope creation, and verify webhook callbacks.', createdAt: '2026-02-20' },
  { id: '7', title: 'Renewal Issue: Access after payment not granted', project: 'eStandard Solutions', projectAbbr: 'ES', projectColor: '#b45309', todoList: 'General', dueOn: null, daysOverdue: -999, isHop: false, workSpec: 'in-sync', workSpecPath: 'hop-studios/clients/estandard/todos/renewal-access-bug/README.md', workSpecModified: '2026-03-01', lastComment: 'On Feb 15 2024, Jerry discussed an issue...', lastCommentAge: '13mo ago', bookmarked: false, assignee: 'Jerry L.', assigneeKind: 'client', description: 'Users who renew their subscription are not automatically granted access. The renewal hook may not be firing correctly in WooCommerce.', createdAt: '2024-02-10' },
  { id: '8', title: '[Approve] Replace outdated Bower with npm for package handling', project: 'eStandard Solutions', projectAbbr: 'ES', projectColor: '#b45309', todoList: 'General', dueOn: '2026-04-15', daysOverdue: -28, isHop: false, workSpec: 'none', lastComment: 'Hey Jerry, One of the issues with your s...', lastCommentAge: '1wk ago', bookmarked: false, assignee: 'Rowan S.', assigneeKind: 'hop', description: 'Bower is deprecated and no longer maintained. Migrate all front-end dependencies to npm. Update build scripts accordingly.', createdAt: '2026-03-05' },
  { id: '9', title: 'Investigate banner rotation logic', project: 'Greater Good', projectAbbr: 'GG', projectColor: '#16a34a', todoList: 'Performance', dueOn: '2026-03-18', daysOverdue: 0, isHop: false, workSpec: 'none', lastComment: null, lastCommentAge: null, bookmarked: false, assignee: 'Travis S.', assigneeKind: 'hop', description: 'The homepage banner rotation may be causing layout shifts and slowing initial load. Investigate current implementation and propose improvements.', createdAt: '2026-03-12' },
  { id: '10', title: 'Redirect domain for PurposeChallenge to correct URL', project: 'Greater Good', projectAbbr: 'GG', projectColor: '#16a34a', todoList: 'General PurposeChallenge.org', dueOn: '2026-03-18', daysOverdue: 0, isHop: false, workSpec: 'stale', workSpecPath: 'clients/greater-good/ggsc/todos/purpose-challenge-redirect/README.md', workSpecModified: '2026-02-28', lastComment: null, lastCommentAge: null, bookmarked: false, assignee: 'Chris B.', assigneeKind: 'client', description: 'PurposeChallenge.org domain needs a 301 redirect to the correct landing page on the Greater Good site. Coordinate with DNS provider.', createdAt: '2026-03-10' },
  { id: '11', title: '[1] Redesign Profile page to account for no-email and no-password', project: 'GGIA', projectAbbr: 'GA', projectColor: '#dc2626', todoList: 'GGIA Bug Fixes', dueOn: '2026-03-19', daysOverdue: -1, isHop: false, workSpec: 'in-sync', workSpecPath: 'clients/greater-good/ggia/todos/8214611243-big-joy-project/profile-redesign.md', workSpecModified: '2026-03-18', lastComment: 'Jason Berry: I have started a...', lastCommentAge: 'Today', bookmarked: true, assignee: 'Jason B.', assigneeKind: 'client', description: 'The profile page assumes all users have both email and password. Social sign-on users may have neither. Redesign to handle these edge cases gracefully.', createdAt: '2026-03-08' },
  { id: '12', title: 'Provide recommendations about social sign-on on GGIA', project: 'GGIA', projectAbbr: 'GA', projectColor: '#dc2626', todoList: 'General', dueOn: '2026-03-17', daysOverdue: 1, isHop: false, workSpec: 'none', lastComment: 'Rowan S.', lastCommentAge: '1d ago', bookmarked: false, assignee: 'Rowan S.', assigneeKind: 'hop', description: 'Evaluate and recommend social sign-on options (Google, Apple, Facebook) for GGIA. Consider privacy implications, user experience, and development effort.', createdAt: '2026-03-05' },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function dueBadgeInfo(todo: MockTodo) {
  if (todo.daysOverdue > 0) {
    const label = todo.daysOverdue === 1 ? '1d overdue'
      : todo.daysOverdue < 7 ? `${todo.daysOverdue}d overdue`
      : todo.daysOverdue < 28 ? `${Math.floor(todo.daysOverdue / 7)}wk overdue`
      : `${Math.floor(todo.daysOverdue / 30)}mo overdue`;
    return { label, cls: 'bg-red-500/15 text-red-700 border-red-300 dark:text-red-400 dark:border-red-700 dark:bg-red-500/10' };
  }
  if (todo.daysOverdue === 0) return { label: 'Due today', cls: 'bg-amber-500/15 text-amber-700 border-amber-300 dark:text-amber-400 dark:border-amber-700' };
  if (!todo.dueOn) return null;
  if (todo.daysOverdue === -1) return { label: 'Tomorrow', cls: 'bg-muted text-muted-foreground border-border' };
  return null;
}

function workSpecBadge(status: MockTodo['workSpec']) {
  if (status === 'in-sync') return <span className="inline-flex items-center gap-1 text-sm text-green-600 dark:text-green-500"><span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />In sync</span>;
  if (status === 'stale') return <span className="inline-flex items-center gap-1 text-sm text-amber-600 dark:text-amber-500"><span className="w-1.5 h-1.5 rounded-full bg-amber-500 inline-block" />Stale</span>;
  return <span className="text-sm text-muted-foreground">No spec</span>;
}

const PROTO_META_LABEL_CLASS =
  'text-xs font-semibold uppercase tracking-wide text-foreground/70 block mb-0.5';

const PROTO_SECTION_LABEL_CLASS =
  'text-xs font-semibold uppercase tracking-wide text-foreground/70';

function formatDue(dueOn: string | null) {
  if (!dueOn) return null;
  const d = new Date(dueOn + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// ─── Context menu items ──────────────────────────────────────────────────────

interface MenuItemDef {
  label: string;
  icon: LucideIcon;
  action: MenuAction;
  disabled?: boolean;
}

function getMenuGroups(todo: MockTodo): MenuItemDef[][] {
  return [
    [
      { label: 'View todo', icon: FileText, action: 'open-flownotes' },
      { label: 'Open in Basecamp', icon: ExternalLink, action: 'open-basecamp' },
      { label: 'Open project', icon: ArrowRight, action: 'open-project' },
    ],
    [
      { label: 'Copy Basecamp URL', icon: Copy, action: 'copy-url' },
      { label: 'Copy todo ID', icon: Hash, action: 'copy-id' },
    ],
    [
      { label: 'View work spec', icon: FileText, action: 'open-workspec', disabled: todo.workSpec === 'none' },
      { label: 'Preview in pane', icon: FileText, action: 'preview-workspec', disabled: todo.workSpec === 'none' },
      { label: 'Refresh from Basecamp', icon: RefreshCw, action: 'refresh-todo' },
    ],
    [
      { label: '+1 week', icon: Calendar, action: 'due-plus-1w', disabled: !todo.dueOn },
      { label: '+2 weeks', icon: Calendar, action: 'due-plus-2w', disabled: !todo.dueOn },
      { label: 'Next Monday', icon: Calendar, action: 'due-next-monday' },
      { label: 'Clear due date', icon: X, action: 'clear-due', disabled: !todo.dueOn },
    ],
    [
      { label: todo.bookmarked ? 'Remove bookmark' : 'Bookmark', icon: Bookmark, action: 'toggle-bookmark' },
    ],
  ];
}

function menuEntriesForTodo(
  todo: MockTodo,
  onAction: (action: MenuAction, todo: MockTodo) => void,
): EntityContextMenuEntry[] {
  const groups = getMenuGroups(todo);
  const entries: EntityContextMenuEntry[] = [];
  groups.forEach((group, gi) => {
    group.forEach((item, ii) => {
      entries.push({
        label: item.label,
        icon: item.icon,
        disabled: item.disabled,
        separatorBefore: ii === 0 && gi > 0,
        onSelect: () => onAction(item.action, todo),
      });
    });
  });
  return entries;
}

function RowOverflowMenu({
  todo,
  onAction,
}: {
  todo: MockTodo;
  onAction: (action: MenuAction, todo: MockTodo) => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground/30 hover:text-foreground group-hover:text-muted-foreground/60">
          <MoreHorizontal className="w-3.5 h-3.5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={4}
        alignOffset={0}
        collisionPadding={QUIET_OVERFLOW_MENU_COLLISION_PADDING}
        className={cn(QUIET_OVERFLOW_MENU_CONTENT_COMPACT_CLASS, 'w-52')}
      >
        {getMenuGroups(todo).flatMap((group, gi) => {
          const items: React.ReactNode[] = [];
          if (gi > 0) items.push(<DropdownMenuSeparator key={`sep-${gi}`} />);
          if (gi === 3) items.push(<DropdownMenuLabel key="due-label" className="text-[10px] text-muted-foreground/60 uppercase tracking-wider">Due date</DropdownMenuLabel>);
          for (const item of group) {
            items.push(
              <DropdownMenuItem
                key={item.action}
                disabled={item.disabled}
                onClick={() => onAction(item.action, todo)}
                className="gap-2 text-xs"
              >
                <item.icon className="w-3.5 h-3.5 flex-none" />
                {item.label}
              </DropdownMenuItem>
            );
          }
          return items;
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ─── Due date cell ───────────────────────────────────────────────────────────

function addDaysToDate(dateStr: string, days: number): string {
  const d = new Date(dateStr + 'T00:00:00');
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function DueDateCell({ todo, onDueDateChange }: {
  todo: MockTodo;
  onDueDateChange: (id: string, newDate: string | null) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(todo.dueOn ?? '');
  const [pos, setPos] = useState<{ x: number; y: number; up: boolean }>({ x: 0, y: 0, up: true });
  const panelRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (editing) setEditValue(todo.dueOn ?? '');
  }, [editing, todo.dueOn]);

  useEffect(() => {
    if (!editing) return;
    const handleClick = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) setEditing(false);
    };
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setEditing(false); };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => { document.removeEventListener('mousedown', handleClick); document.removeEventListener('keydown', handleKey); };
  }, [editing]);

  const badge = dueBadgeInfo(todo);
  const isDirty = editValue !== (todo.dueOn ?? '');

  const handleOpen = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      const up = rect.top > 160;
      setPos({
        x: Math.min(rect.left, window.innerWidth - 200),
        y: up ? rect.top : rect.bottom,
        up,
      });
    }
    setEditing(true);
  };

  const handleSave = () => {
    onDueDateChange(todo.id, editValue || null);
    setEditing(false);
  };

  const chipContent = badge ? (
    <Badge className={cn('text-[10px] px-1.5 py-0 border cursor-pointer', badge.cls)}>{badge.label}</Badge>
  ) : todo.dueOn ? (
    <span className="text-xs text-muted-foreground group-hover/due:text-foreground transition-colors">{formatDue(todo.dueOn)}</span>
  ) : (
    <span className="text-xs text-muted-foreground/30 group-hover/due:text-muted-foreground transition-colors">Set date</span>
  );

  return (
    <div ref={anchorRef}>
      <button onClick={handleOpen} className="text-left w-full group/due" title="Click to edit due date">
        {chipContent}
      </button>
      {editing && (
        <div
          ref={panelRef}
          className="fixed z-50 bg-popover border rounded-lg shadow-lg p-2.5 w-[11rem] space-y-1.5 animate-in fade-in-0 zoom-in-95"
          style={{
            left: pos.x,
            ...(pos.up ? { bottom: window.innerHeight - pos.y + 4 } : { top: pos.y + 4 }),
          }}
          onClick={e => e.stopPropagation()}
        >
          <input
            type="date"
            value={editValue}
            onChange={e => setEditValue(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && isDirty) handleSave(); }}
            className="h-7 w-full px-1.5 rounded border border-input bg-background text-xs [color-scheme:light] dark:[color-scheme:dark]"
            autoFocus
          />
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => {
                const base = editValue || todo.dueOn;
                if (base) setEditValue(addDaysToDate(base, 7));
              }}
              disabled={!editValue && !todo.dueOn}
              className="h-7 flex-1 px-2 rounded border border-border text-xs text-muted-foreground hover:bg-accent hover:text-foreground transition-colors whitespace-nowrap disabled:opacity-40"
            >
              +1 week
            </button>
            <button
              onClick={handleSave}
              disabled={!isDirty}
              className="h-7 flex-1 rounded bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 transition-colors disabled:opacity-30 disabled:pointer-events-none"
            >
              Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Work spec popover ───────────────────────────────────────────────────────

function WorkSpecPopover({ todo, onPreviewInPanel }: {
  todo: MockTodo;
  onPreviewInPanel: (todo: MockTodo) => void;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ x: number; y: number; up: boolean }>({ x: 0, y: 0, up: true });
  const panelRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) setOpen(false);
    };
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => { document.removeEventListener('mousedown', handleClick); document.removeEventListener('keydown', handleKey); };
  }, [open]);

  const handleOpen = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (todo.workSpec === 'none') return;
    if (anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      const up = rect.top > 220;
      setPos({
        x: Math.min(rect.left, window.innerWidth - 280),
        y: up ? rect.top : rect.bottom,
        up,
      });
    }
    setOpen(true);
  };

  const fileName = todo.workSpecPath?.split('/').pop() ?? '';

  return (
    <div ref={anchorRef}>
      <button onClick={handleOpen} className="text-left w-full">
        {workSpecBadge(todo.workSpec)}
      </button>
      {open && todo.workSpecPath && (
        <div
          ref={panelRef}
          className="fixed z-50 bg-popover border rounded-lg shadow-lg p-3 w-[16rem] space-y-2.5 animate-in fade-in-0 zoom-in-95"
          style={{
            left: pos.x,
            ...(pos.up ? { bottom: window.innerHeight - pos.y + 4 } : { top: pos.y + 4 }),
          }}
          onClick={e => e.stopPropagation()}
        >
          <div className="flex items-start gap-2">
            <FileText className="w-4 h-4 flex-none text-muted-foreground mt-0.5" />
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium truncate" title={fileName}>{fileName}</p>
              <p className="text-[10px] text-muted-foreground/60 truncate mt-0.5" title={todo.workSpecPath}>
                {todo.workSpecPath}
              </p>
            </div>
          </div>

          <div className="space-y-1 text-[11px] text-muted-foreground">
            <div className="flex items-center gap-1.5">
              <Clock3 className="w-3 h-3 flex-none" />
              <span>Modified {todo.workSpecModified ? formatDue(todo.workSpecModified) : 'unknown'}</span>
            </div>
            <div className="flex items-center gap-1.5">
              {todo.workSpec === 'in-sync' ? (
                <><Check className="w-3 h-3 flex-none text-green-500" /><span className="text-green-600 dark:text-green-500">In sync with Basecamp</span></>
              ) : (
                <><RefreshCw className="w-3 h-3 flex-none text-amber-500" /><span className="text-amber-600 dark:text-amber-500">Stale - needs update</span></>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              <FolderOpen className="w-3 h-3 flex-none" />
              <span>{todo.project}</span>
            </div>
          </div>

          <div className="flex gap-1.5 pt-0.5">
            <button
              onClick={() => { onPreviewInPanel(todo); setOpen(false); }}
              className="flex-1 h-7 rounded bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 transition-colors flex items-center justify-center gap-1"
            >
              <Eye className="w-3 h-3" /> Preview
            </button>
            <button
              onClick={() => { console.log('[mock] Open work spec tab:', todo.workSpecPath); setOpen(false); }}
              className="h-7 px-2.5 rounded border border-border text-xs text-muted-foreground hover:bg-accent hover:text-foreground transition-colors flex items-center gap-1"
            >
              <Pencil className="w-3 h-3" /> Open
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Secondary panel (right side preview) ────────────────────────────────────

type PanelContent =
  | { type: 'todo-detail'; todo: MockTodo }
  | { type: 'work-spec'; todo: MockTodo };

function SecondaryPanel({ content, onClose, onRefresh, onOpenBasecamp }: {
  content: PanelContent;
  onClose: () => void;
  onRefresh: () => void;
  onOpenBasecamp: () => void;
}) {
  return (
    <div
      className="h-full bg-card flex flex-col"
      data-testid={testIds.basecampTodosTest.previewPane}
    >
      {content.type === 'todo-detail' ? (
        <TodoDetailView
          todo={content.todo}
          onClose={onClose}
          onRefresh={onRefresh}
          onOpenBasecamp={onOpenBasecamp}
        />
      ) : (
        <WorkSpecPreview todo={content.todo} onClose={onClose} />
      )}
    </div>
  );
}

function TodoDetailView({ todo, onClose, onRefresh, onOpenBasecamp }: {
  todo: MockTodo;
  onClose: () => void;
  onRefresh: () => void;
  onOpenBasecamp: () => void;
}) {
  const badge = dueBadgeInfo(todo);

  return (
    <>
      <PreviewPaneHeader
        icon={<FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />}
        label="Todo Preview"
        onOpen={onOpenBasecamp}
        openTitle="Open in Basecamp"
        onClose={onClose}
      />

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <div className="space-y-3">
          <nav
            className="flex min-w-0 items-center gap-1 text-xs text-muted-foreground"
            aria-label="Todo location"
            data-testid={testIds.basecampTodosTest.previewBreadcrumb}
          >
            <span className="truncate max-w-[140px]" title={todo.project}>{todo.project}</span>
            <ChevronRight className="h-3 w-3 shrink-0 opacity-40" aria-hidden />
            <span className="truncate max-w-[160px]" title={todo.todoList}>{todo.todoList}</span>
          </nav>
          <h2 className="text-base font-semibold leading-snug text-foreground">{todo.title}</h2>
        </div>

        <div
          className="grid grid-cols-2 gap-y-2.5 gap-x-4 text-sm"
          data-testid={testIds.basecampTodosTest.previewMeta}
        >
          <div>
            <span className={PROTO_META_LABEL_CLASS}>Assignee</span>
            <span className="flex items-center gap-1 text-foreground"><User2 className="w-3.5 h-3.5 text-muted-foreground" />{todo.assignee ?? 'Unassigned'}</span>
          </div>
          <div>
            <span className={PROTO_META_LABEL_CLASS}>List</span>
            <span className="text-foreground">{todo.todoList}</span>
          </div>
          <div>
            <span className={PROTO_META_LABEL_CLASS}>Due date</span>
            {badge ? (
              <Badge className={cn('text-[11px] px-1.5 py-0 border font-medium', badge.cls)}>{badge.label}</Badge>
            ) : todo.dueOn ? (
              <span className="text-foreground">{formatDue(todo.dueOn)}</span>
            ) : (
              <span className="text-muted-foreground">No due date</span>
            )}
          </div>
          <div>
            <span className={PROTO_META_LABEL_CLASS}>Created</span>
            <span className="text-foreground">{todo.createdAt ? formatDue(todo.createdAt) : 'Unknown'}</span>
          </div>
          <div>
            <span className={PROTO_META_LABEL_CLASS}>Work spec</span>
            {workSpecBadge(todo.workSpec)}
          </div>
          <div>
            <span className={PROTO_META_LABEL_CLASS}>Bookmarked</span>
            <span className="text-foreground">{todo.bookmarked ? 'Yes' : 'No'}</span>
          </div>
        </div>

        {todo.description && (
          <div>
            <span className={cn(PROTO_SECTION_LABEL_CLASS, 'block mb-1.5')}>Description</span>
            <div className="text-sm leading-relaxed text-foreground bg-muted/30 rounded-md p-3 border border-border/50">
              {todo.description}
            </div>
          </div>
        )}

        {todo.lastComment && (
          <div>
            <span className={cn(PROTO_SECTION_LABEL_CLASS, 'block mb-1.5')}>Latest comment</span>
            <div className="text-sm text-foreground bg-muted/30 rounded-md p-3 border border-border/50">
              <span className="text-muted-foreground text-xs">{todo.lastCommentAge}</span>
              <p className="mt-1">&ldquo;{todo.lastComment}&rdquo;</p>
            </div>
          </div>
        )}
      </div>

      <div
        className={cn(
          SHELL_COLUMN_FOOTER_CLASS,
          'flex shrink-0 items-center gap-1 px-2 border-t border-border bg-muted/30',
        )}
        data-testid={testIds.basecampTodosTest.previewFooter}
      >
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-xs gap-1.5 text-muted-foreground"
          onClick={onRefresh}
          data-testid={testIds.basecampTodosTest.previewRefresh}
        >
          <RefreshCw className="w-3.5 h-3.5" />
          Refresh
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-xs gap-1.5 text-muted-foreground"
          onClick={onOpenBasecamp}
          data-testid={testIds.basecampTodosTest.previewOpenBasecamp}
        >
          <ExternalLink className="w-3.5 h-3.5" />
          Basecamp
        </Button>
      </div>
    </>
  );
}

function WorkSpecPreview({ todo, onClose }: {
  todo: MockTodo;
  onClose: () => void;
}) {
  const fileName = todo.workSpecPath?.split('/').pop() ?? 'Unknown';

  return (
    <>
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border bg-muted/30 flex-shrink-0">
        <span className="text-[11px] font-medium text-muted-foreground/70 uppercase tracking-wider">Work Spec</span>
        <ChevronRight className="w-3 h-3 text-muted-foreground/40" />
        <span className="text-[11px] font-medium text-foreground truncate" title={fileName}>{fileName}</span>
        <div className="flex-1" />
        <button
          onClick={() => console.log('[mock] Open work spec in tab:', todo.workSpecPath)}
          className="h-6 px-2 rounded text-[11px] text-muted-foreground hover:bg-accent hover:text-foreground transition-colors flex items-center gap-1"
        >
          Open <Pencil className="w-3 h-3" />
        </button>
        <button onClick={onClose} className="h-6 w-6 rounded hover:bg-accent flex items-center justify-center transition-colors">
          <X className="w-3.5 h-3.5 text-muted-foreground" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <div className="flex items-start gap-2.5">
          <FileText className="w-5 h-5 flex-none text-muted-foreground mt-0.5" />
          <div>
            <h2 className="text-base font-semibold leading-snug">{todo.title}</h2>
            <p className="text-xs text-muted-foreground/60 mt-1 font-mono truncate" title={todo.workSpecPath}>
              {todo.workSpecPath}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-y-2.5 gap-x-4 text-sm">
          <div>
            <span className={PROTO_META_LABEL_CLASS}>Status</span>
            {workSpecBadge(todo.workSpec)}
          </div>
          <div>
            <span className={PROTO_META_LABEL_CLASS}>Last modified</span>
            <span className="text-foreground">{todo.workSpecModified ? formatDue(todo.workSpecModified) : 'Unknown'}</span>
          </div>
          <div>
            <span className={PROTO_META_LABEL_CLASS}>Project</span>
            <span className="text-foreground">{todo.project}</span>
          </div>
          <div>
            <span className={PROTO_META_LABEL_CLASS}>Linked todo</span>
            <span className="truncate block text-foreground" title={todo.title}>{todo.title}</span>
          </div>
        </div>

        <div>
          <span className={cn(PROTO_SECTION_LABEL_CLASS, 'block mb-1.5')}>Preview</span>
          <div className="text-sm leading-relaxed text-foreground/80 bg-muted/30 rounded-md p-4 border border-border/50 space-y-3">
            <h3 className="text-sm font-semibold"># {todo.title}</h3>
            <p className="text-xs text-muted-foreground">
              {todo.description ?? 'No description available.'}
            </p>
            <div className="border-t border-border/50 pt-2 text-xs text-muted-foreground/50 italic">
              [Mock preview - in production this renders the full document content]
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Table header ────────────────────────────────────────────────────────────

function SortIndicator({ column, sort }: { column: SortColumn; sort: SortState | null }) {
  if (!sort || sort.column !== column) return null;
  return sort.dir === 'asc'
    ? <ArrowUp className="w-3 h-3 inline-block ml-0.5 text-foreground/60" />
    : <ArrowDown className="w-3 h-3 inline-block ml-0.5 text-foreground/60" />;
}

const COLUMN_SORT_MAP: Record<string, SortColumn> = {
  todolist: 'todolist', assignee: 'assignee', workSpec: 'workSpec', activity: 'activity', due: 'due',
};

function TodoTableHeader({ gridTemplate, visible, sort, onSort }: {
  gridTemplate: string;
  visible: Set<string>;
  sort: SortState | null;
  onSort: (col: SortColumn) => void;
}) {
  const hdrCls = 'px-2 truncate cursor-pointer hover:text-foreground transition-colors flex items-center gap-0.5';
  return (
    <div
      className="grid items-center h-8 border-b border-border bg-muted/50 text-[11px] font-medium text-muted-foreground/70 uppercase tracking-wider sticky top-0 z-10 select-none"
      style={{ gridTemplateColumns: gridTemplate }}
    >
      <div />
      <button className="px-1 text-left cursor-pointer hover:text-foreground transition-colors flex items-center" onClick={() => onSort('project')}>
        Proj<SortIndicator column="project" sort={sort} />
      </button>
      <button className={hdrCls} onClick={() => onSort('title')}>
        Title<SortIndicator column="title" sort={sort} />
      </button>
      {COLUMNS.map(col => visible.has(col.id) ? (
        <button key={col.id} className={hdrCls} onClick={() => onSort(COLUMN_SORT_MAP[col.id] as SortColumn)}>
          {col.label}<SortIndicator column={COLUMN_SORT_MAP[col.id] as SortColumn} sort={sort} />
        </button>
      ) : null)}
      <div />
    </div>
  );
}

// ─── Table row ───────────────────────────────────────────────────────────────

function TodoTableRow({ todo, gridTemplate, visible, onAction, onDueDateChange, onContextMenu: onCtx, onTitleClick, onSpecClick, isActive, hideProjectChrome }: {
  todo: MockTodo;
  gridTemplate: string;
  visible: Set<string>;
  onAction: (action: MenuAction, todo: MockTodo) => void;
  onDueDateChange: (id: string, newDate: string | null) => void;
  onContextMenu: (e: React.MouseEvent, todo: MockTodo) => void;
  onTitleClick: (todo: MockTodo) => void;
  onSpecClick: (todo: MockTodo) => void;
  isActive?: boolean;
  hideProjectChrome?: boolean;
}) {
  const isOverdue = todo.daysOverdue > 0;

  return (
    <div
      className={cn(
        'group grid items-center border-b border-border/50 transition-colors',
        'hover:bg-accent/40',
        isActive && 'bg-primary/5 hover:bg-primary/10',
        isOverdue && !isActive && 'bg-red-500/[0.03] hover:bg-red-500/[0.07]',
      )}
      style={{ gridTemplateColumns: gridTemplate }}
      onContextMenu={e => onCtx(e, todo)}
    >
      {!hideProjectChrome ? (
        <>
          <div className="self-stretch" style={{ backgroundColor: todo.projectColor, opacity: 0.7 }} />
          <div className="px-1 py-1.5 flex items-center justify-center">
            <span
              className="w-7 h-5 rounded text-[10px] font-bold flex items-center justify-center text-white flex-none"
              style={{ backgroundColor: todo.projectColor }}
              title={todo.project}
            >
              {todo.projectAbbr}
            </span>
          </div>
        </>
      ) : null}

      <div className="px-2 py-1.5 min-w-0">
        <button
          onClick={(e) => { e.stopPropagation(); onTitleClick(todo); }}
          className="text-sm font-medium truncate block leading-snug text-left w-full hover:text-primary hover:underline underline-offset-2 transition-colors cursor-pointer"
          title={todo.title}
        >
          {todo.title}
        </button>
      </div>

      {visible.has('todolist') && (
        <div className="px-2 py-1.5 text-xs text-muted-foreground/60 truncate" title={todo.todoList}>
          {todo.todoList}
        </div>
      )}
      {visible.has('assignee') && (
        <div className="px-2 py-1.5 text-xs text-muted-foreground truncate" title={todo.assignee ?? 'Unassigned'}>
          {todo.assignee ? (
            <span className="inline-flex items-center gap-1">
              <User2 className="w-3 h-3 text-muted-foreground/50 flex-none" />
              <span className="truncate">{todo.assignee}</span>
            </span>
          ) : (
            <span className="text-muted-foreground/30">Unassigned</span>
          )}
        </div>
      )}
      {visible.has('workSpec') && (
        <div className="px-2 py-1.5" onClick={e => e.stopPropagation()}>
          <WorkSpecPopover todo={todo} onPreviewInPanel={() => onSpecClick(todo)} />
        </div>
      )}
      {visible.has('activity') && (
        <div className="px-2 py-1.5 text-xs text-muted-foreground/50 truncate">
          {todo.lastComment ? (
            <span title={todo.lastComment}>
              <span className="text-muted-foreground/70">{todo.lastCommentAge}</span>
              {' '}&middot; &ldquo;{todo.lastComment.slice(0, 28)}&hellip;&rdquo;
            </span>
          ) : <span className="text-muted-foreground/25">&mdash;</span>}
        </div>
      )}
      {visible.has('due') && (
        <div className="px-2 py-1.5" onClick={e => e.stopPropagation()}>
          <DueDateCell todo={todo} onDueDateChange={onDueDateChange} />
        </div>
      )}

      <div className="px-1 py-1.5 flex items-center justify-center" onClick={e => e.stopPropagation()}>
        <RowOverflowMenu todo={todo} onAction={onAction} />
      </div>
    </div>
  );
}

// ─── View options (column show/hide) ─────────────────────────────────────────

function ViewOptionsDropdown({ visible, onToggle }: { visible: Set<string>; onToggle: (id: string) => void }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-7 w-7" title="Columns">
          <Columns3 className="w-3.5 h-3.5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        sideOffset={4}
        alignOffset={0}
        collisionPadding={QUIET_OVERFLOW_MENU_COLLISION_PADDING}
        className={cn(QUIET_OVERFLOW_MENU_CONTENT_COMPACT_CLASS, 'w-44')}
      >
        <DropdownMenuLabel className="text-[10px] text-muted-foreground/60 uppercase tracking-wider">Show columns</DropdownMenuLabel>
        {COLUMNS.filter(c => c.canHide).map(col => (
          <DropdownMenuItem key={col.id} onClick={() => onToggle(col.id)} className="gap-2 text-xs">
            <Check className={cn('w-3.5 h-3.5 text-muted-foreground', visible.has(col.id) ? 'opacity-100' : 'opacity-0')} />
            {col.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ─── By-Project view ─────────────────────────────────────────────────────────

interface ProjectGroup {
  project: string;
  projectAbbr: string;
  projectColor: string;
  lists: { listName: string; todos: MockTodo[] }[];
}

function groupByProject(todos: MockTodo[]): ProjectGroup[] {
  const map = new Map<string, ProjectGroup>();
  for (const todo of todos) {
    if (!map.has(todo.project)) {
      map.set(todo.project, { project: todo.project, projectAbbr: todo.projectAbbr, projectColor: todo.projectColor, lists: [] });
    }
    const group = map.get(todo.project)!;
    const list = group.lists.find(l => l.listName === todo.todoList);
    if (list) list.todos.push(todo);
    else group.lists.push({ listName: todo.todoList, todos: [todo] });
  }
  return Array.from(map.values());
}

function ByProjectView({ todos, visible, gridTemplate, onAction, onContextMenu: onCtx, onDueDateChange, onTitleClick, onSpecClick, activeTodoId }: {
  todos: MockTodo[];
  visible: Set<string>;
  gridTemplate: string;
  onAction: (action: MenuAction, todo: MockTodo) => void;
  onContextMenu: (e: React.MouseEvent, todo: MockTodo) => void;
  onDueDateChange: (id: string, newDate: string | null) => void;
  onTitleClick: (todo: MockTodo) => void;
  onSpecClick: (todo: MockTodo) => void;
  activeTodoId?: string;
}) {
  const groups = groupByProject(todos);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const toggle = (key: string) => setCollapsed(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });

  return (
    <div>
      {groups.map(group => (
        <div key={group.project} className="border-b border-border last:border-0">
          <button onClick={() => toggle(group.project)} className="w-full flex items-center gap-2 px-3 py-2 hover:bg-accent/30 transition-colors">
            <div className="w-0.5 h-4 rounded-full flex-none" style={{ backgroundColor: group.projectColor }} />
            <span className="flex-none w-6 h-[18px] rounded text-[9px] font-bold flex items-center justify-center text-white" style={{ backgroundColor: group.projectColor }}>{group.projectAbbr}</span>
            <span className="flex-1 text-left text-sm font-semibold text-foreground/90">{group.project}</span>
            <Badge variant="secondary" className="flex-none text-[10px] px-1.5">{group.lists.reduce((n, l) => n + l.todos.length, 0)}</Badge>
            <ChevronRight className={cn('w-3.5 h-3.5 text-muted-foreground transition-transform', !collapsed.has(group.project) && 'rotate-90')} />
          </button>
          {!collapsed.has(group.project) && (
            <div className="pl-3">
              {group.lists.map(list => (
                <div key={list.listName}>
                  <div className="flex items-center gap-1.5 px-3 py-1 border-t border-border/40">
                    <span className="text-[11px] font-medium text-muted-foreground/70 uppercase tracking-wide">{list.listName}</span>
                    <span className="text-[10px] text-muted-foreground/40">({list.todos.length})</span>
                  </div>
                  {list.todos.map(todo => (
                    <TodoTableRow
                      key={todo.id}
                      todo={todo}
                      gridTemplate={gridTemplate}
                      visible={visible}
                      hideProjectChrome
                      onAction={onAction}
                      onDueDateChange={onDueDateChange}
                      onContextMenu={onCtx}
                      onTitleClick={onTitleClick}
                      onSpecClick={onSpecClick}
                      isActive={activeTodoId === todo.id}
                    />
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Main prototype ───────────────────────────────────────────────────────────

export function TestBasecampTodos() {
  const { theme, setTheme } = useTheme();
  const [railMode, setRailMode] = useState<ProtoIconRailActive>('documents');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('by-date');
  const [scope, setScope] = useState<ScopeFilter>('all');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [assigneeFacet, setAssigneeFacet] = useState<AssigneeFacet>('all');
  const [assigneePerson, setAssigneePerson] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [todos, setTodos] = useState<MockTodo[]>(MOCK);
  const { visible, toggle: toggleColumn, gridTemplate, groupedGridTemplate } = useColumnVisibility();
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; todo: MockTodo } | null>(null);
  const [sort, setSort] = useState<SortState | null>(null);
  const [panelContent, setPanelContent] = useState<PanelContent | null>(null);

  const peopleOptions = useMemo(
    () =>
      groupAssigneeNames(
        todos.map((t) => ({
          assignees: t.assignee
            ? [{
                id: t.assignee,
                name: t.assignee,
                client: t.assigneeKind === 'client',
                email: t.assigneeKind === 'hop' ? `${t.assignee}@hopstudios.com` : undefined,
              }]
            : [],
        })),
      ),
    [todos],
  );

  const handleSort = useCallback((col: SortColumn) => {
    setSort(prev => {
      if (prev?.column === col) return prev.dir === 'asc' ? { column: col, dir: 'desc' } : null;
      return { column: col, dir: 'asc' };
    });
  }, []);

  const filtered = useMemo(() => {
    let items = todos;
    if (scope === 'hop') items = items.filter(t => t.isHop);
    if (scope === 'client') items = items.filter(t => !t.isHop);
    if (status === 'overdue') items = items.filter(t => t.daysOverdue > 0);
    if (status === 'due-today') items = items.filter(t => t.daysOverdue === 0);

    if (assigneeFacet === 'me') {
      items = items.filter(t => t.assignee === PROTOTYPE_ME);
    } else if (assigneeFacet === 'hop') {
      items = items.filter(t => t.assigneeKind === 'hop' || (t.assignee != null && HOP_ASSIGNEE_NAMES.has(t.assignee)));
    } else if (assigneeFacet === 'clients') {
      items = items.filter(t => t.assigneeKind === 'client' || (t.assignee != null && !HOP_ASSIGNEE_NAMES.has(t.assignee)));
    } else if (assigneeFacet === 'unassigned') {
      items = items.filter(t => !t.assignee);
    } else if (assigneeFacet === 'person' && assigneePerson) {
      items = items.filter(t => t.assignee === assigneePerson);
    }

    if (search) items = items.filter(t =>
      t.title.toLowerCase().includes(search.toLowerCase()) ||
      t.project.toLowerCase().includes(search.toLowerCase()) ||
      (t.assignee ?? '').toLowerCase().includes(search.toLowerCase())
    );

    const sorted = [...items];
    if (sort) {
      const dir = sort.dir === 'asc' ? 1 : -1;
      sorted.sort((a, b) => {
        let cmp = 0;
        switch (sort.column) {
          case 'project': cmp = a.project.localeCompare(b.project); break;
          case 'title': cmp = a.title.localeCompare(b.title); break;
          case 'todolist': cmp = a.todoList.localeCompare(b.todoList); break;
          case 'assignee': cmp = (a.assignee ?? '').localeCompare(b.assignee ?? ''); break;
          case 'workSpec': cmp = (a.workSpec ?? '').localeCompare(b.workSpec ?? ''); break;
          case 'activity': cmp = (a.lastCommentAge ?? '').localeCompare(b.lastCommentAge ?? ''); break;
          case 'due': {
            if (!a.dueOn && !b.dueOn) cmp = 0;
            else if (!a.dueOn) cmp = 1;
            else if (!b.dueOn) cmp = -1;
            else cmp = a.dueOn.localeCompare(b.dueOn);
            break;
          }
        }
        return cmp * dir;
      });
    } else {
      sorted.sort((a, b) => {
        if (a.daysOverdue > 0 && b.daysOverdue <= 0) return -1;
        if (b.daysOverdue > 0 && a.daysOverdue <= 0) return 1;
        if (!a.dueOn && b.dueOn) return 1;
        if (a.dueOn && !b.dueOn) return -1;
        if (a.dueOn && b.dueOn) return a.dueOn.localeCompare(b.dueOn);
        return 0;
      });
    }
    return sorted;
  }, [todos, scope, status, search, sort, assigneeFacet, assigneePerson]);

  const handleDueDateChange = useCallback((id: string, newDate: string | null) => {
    setTodos(prev => prev.map(t => {
      if (t.id !== id) return t;
      if (!newDate) return { ...t, dueOn: null, daysOverdue: -999 };
      const due = new Date(newDate + 'T00:00:00');
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const diff = Math.round((today.getTime() - due.getTime()) / 86400000);
      return { ...t, dueOn: newDate, daysOverdue: diff };
    }));
  }, []);

  function addDays(dateStr: string, days: number): string {
    const d = new Date(dateStr + 'T00:00:00');
    d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 10);
  }
  function nextMonday(): string {
    const d = new Date(); d.setHours(0, 0, 0, 0);
    const day = d.getDay();
    d.setDate(d.getDate() + (day === 0 ? 1 : 8 - day));
    return d.toISOString().slice(0, 10);
  }

  const handleTitleClick = useCallback((todo: MockTodo) => {
    setPreviewOpen(true);
    setPanelContent({ type: 'todo-detail', todo });
  }, []);

  const handleSpecClick = useCallback((todo: MockTodo) => {
    if (todo.workSpec !== 'none' && todo.workSpecPath) {
      setPanelContent({ type: 'work-spec', todo });
    }
  }, []);

  const handleAction = useCallback((action: MenuAction, todo: MockTodo) => {
    switch (action) {
      case 'open-flownotes': setPanelContent({ type: 'todo-detail', todo }); break;
      case 'open-basecamp': console.log('[mock] Open in Basecamp:', todo.id); break;
      case 'open-project': console.log('[mock] Open project:', todo.project); break;
      case 'copy-url': navigator.clipboard?.writeText(`https://app.basecamp.com/todo/${todo.id}`); console.log('[mock] Copied URL'); break;
      case 'copy-id': navigator.clipboard?.writeText(todo.id); console.log('[mock] Copied ID:', todo.id); break;
      case 'open-workspec': console.log('[mock] Open work spec for:', todo.title); break;
      case 'preview-workspec': if (todo.workSpec !== 'none') setPanelContent({ type: 'work-spec', todo }); break;
      case 'refresh-todo': console.log('[mock] Refresh from Basecamp:', todo.id); break;
      case 'due-plus-1w': if (todo.dueOn) handleDueDateChange(todo.id, addDays(todo.dueOn, 7)); break;
      case 'due-plus-2w': if (todo.dueOn) handleDueDateChange(todo.id, addDays(todo.dueOn, 14)); break;
      case 'due-next-monday': handleDueDateChange(todo.id, nextMonday()); break;
      case 'clear-due': handleDueDateChange(todo.id, null); break;
      case 'toggle-bookmark': setTodos(prev => prev.map(t => t.id === todo.id ? { ...t, bookmarked: !t.bookmarked } : t)); break;
    }
  }, [handleDueDateChange]);

  const handleContextMenu = useCallback((e: React.MouseEvent, todo: MockTodo) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, todo });
  }, []);

  const FilterBtn = ({ value, label, current, onClick }: { value: string; label: string; current: string; onClick: () => void }) => (
    <button
      onClick={onClick}
      className={cn(
        'px-3 py-1 text-xs rounded-full border transition-colors whitespace-nowrap',
        value === current
          ? 'bg-foreground text-background border-foreground'
          : 'border-border text-muted-foreground hover:border-foreground/40 hover:text-foreground'
      )}
    >
      {label}
    </button>
  );

  const crumbSegments: BreadcrumbSegment[] = [
    { label: 'Todos', path: null },
  ]

  return (
    <TooltipProvider delayDuration={200}>
    <div
      className="flex h-screen min-h-screen flex-col bg-background text-foreground antialiased"
      style={maaterProtoAppSkinStyle()}
      data-maater-gallery-skin="app"
      data-testid={testIds.basecampTodosTest.root}
    >
      <ProtoAppHeader
        themeControl="light-dark"
        appTheme={theme}
        onAppTheme={(t) => {
          void setTheme(t)
        }}
        badge="basecamp-todos"
        testId={testIds.basecampTodosTest.appHeader}
        prototypeIndexTestId={testIds.basecampTodosTest.backToPrototypeIndex}
      />

      <div className="flex min-h-0 flex-1">
        <ProtoIconRail
          active={railMode}
          suggested={railMode === 'documents' ? 'basecamp' : null}
          onSelect={setRailMode}
          testId={testIds.basecampTodosTest.iconRail}
        />
        <ProtoModePanel
          title={railMode === 'basecamp' ? 'Basecamp' : 'Browse'}
          subtitle={railMode === 'basecamp' ? 'Mode panel after rail click' : 'My documents'}
          testId={testIds.basecampTodosTest.modePanel}
        >
          {railMode === 'basecamp' ? (
            <SidebarTreeRow
              padLeft={8}
              icon={<FileText className="h-3.5 w-3.5" />}
              label="Todos"
              active
            />
          ) : (
            <SidebarTreeRow
              padLeft={8}
              icon={<FileText className="h-3.5 w-3.5" />}
              label="My documents"
            />
          )}
        </ProtoModePanel>

        <main className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <ProtoTabRow
            tabs={[{
              id: 'todos',
              label: 'Todos',
              icon: <BasecampIcon className="h-3.5 w-3.5 shrink-0" />,
              testId: testIds.basecampTodosTest.tabTodos,
            }]}
            activeId="todos"
            onSelect={() => undefined}
            testId={testIds.basecampTodosTest.tabRow}
          />
          <div
            className="flex items-center gap-1 border-b border-border px-2 py-1.5"
            data-testid={testIds.basecampTodosTest.chromeStrip}
          >
            <NavigationButtons canGoBack={false} canGoForward={false} />
            <div className="min-w-0 flex-1 px-1">
              <AddressBar segments={crumbSegments} className="text-xs" />
            </div>
            <PreviewPanelToggle
              open={previewOpen}
              onToggle={() => setPreviewOpen((v) => !v)}
              testId={testIds.basecampTodosTest.previewToggle}
            />
          </div>

          <div className="border-b border-border px-4 py-2 flex items-center gap-3 flex-wrap flex-shrink-0">
            <div className="flex items-center border border-border rounded-md overflow-hidden text-xs">
              <button onClick={() => setViewMode('by-date')} className={cn('flex items-center gap-1.5 px-3 py-1.5 transition-colors', viewMode === 'by-date' ? 'bg-foreground text-background' : 'hover:bg-accent')}>
                <Calendar className="w-3.5 h-3.5" /> By date
              </button>
              <button onClick={() => setViewMode('by-project')} className={cn('flex items-center gap-1.5 px-3 py-1.5 border-l border-border transition-colors', viewMode === 'by-project' ? 'bg-foreground text-background' : 'hover:bg-accent')}>
                <Layers className="w-3.5 h-3.5" /> By project
              </button>
            </div>
            <ViewOptionsDropdown visible={visible} onToggle={toggleColumn} />
            <Badge variant="secondary" className="text-xs">{filtered.length}</Badge>
            <div className="flex-1" />
            <Button variant="ghost" size="icon" className="h-8 w-8"><RefreshCw className="w-3.5 h-3.5" /></Button>
          </div>

          <div className="border-b border-border px-4 py-2 flex items-center gap-2 flex-wrap flex-shrink-0">
            <FilterBtn value="all" label="All" current={scope} onClick={() => setScope('all')} />
            <FilterBtn value="hop" label="Hop internal" current={scope} onClick={() => setScope('hop')} />
            <FilterBtn value="client" label="Client" current={scope} onClick={() => setScope('client')} />
            <div className="w-px h-4 bg-border mx-1" />
            <FilterBtn value="all" label="All" current={status} onClick={() => setStatus('all')} />
            <FilterBtn value="overdue" label="Overdue" current={status} onClick={() => setStatus('overdue')} />
            <FilterBtn value="due-today" label="Due today" current={status} onClick={() => setStatus('due-today')} />
            <div className="flex-1" />
            <TodosAssigneeCombobox
              facet={assigneeFacet}
              person={assigneePerson}
              people={peopleOptions}
              onChange={(facet, person) => {
                setAssigneeFacet(facet)
                setAssigneePerson(person)
              }}
            />
            <div className="relative w-48">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." className="h-7 pl-8 text-xs" />
            </div>
          </div>

          <div className="flex-1 flex overflow-hidden">
            <div className={cn('flex min-h-0 min-w-0 flex-1 flex-col', previewOpen && 'min-w-0')}>
              <div className="min-h-0 flex-1 overflow-auto">
                {viewMode === 'by-date' && (
                  <div className="w-full">
                    <TodoTableHeader gridTemplate={gridTemplate} visible={visible} sort={sort} onSort={handleSort} />
                    {filtered.length === 0 ? (
                      <div className="text-center text-muted-foreground py-12 text-sm">No todos match filters.</div>
                    ) : (
                      filtered.map(t => (
                        <TodoTableRow
                          key={t.id}
                          todo={t}
                          gridTemplate={gridTemplate}
                          visible={visible}
                          onAction={handleAction}
                          onDueDateChange={handleDueDateChange}
                          onContextMenu={handleContextMenu}
                          onTitleClick={handleTitleClick}
                          onSpecClick={handleSpecClick}
                          isActive={panelContent?.todo.id === t.id}
                        />
                      ))
                    )}
                  </div>
                )}

                {viewMode === 'by-project' && (
                  <ByProjectView
                    todos={filtered}
                    visible={visible}
                    gridTemplate={groupedGridTemplate}
                    onAction={handleAction}
                    onContextMenu={handleContextMenu}
                    onDueDateChange={handleDueDateChange}
                    onTitleClick={handleTitleClick}
                    onSpecClick={handleSpecClick}
                    activeTodoId={panelContent?.todo.id}
                  />
                )}
              </div>
              <ListSurfaceStatusBar
                includePreviewTip
                data-testid={testIds.basecampTodosTest.statusBar}
                trailing={
                  <span className="truncate">
                    {filtered.length} todo{filtered.length === 1 ? '' : 's'}
                  </span>
                }
              />
            </div>

            {previewOpen ? (
              <div className="w-[380px] flex-shrink-0 border-l border-border min-h-0">
                {panelContent ? (
                  <SecondaryPanel
                    content={panelContent}
                    onClose={() => { setPanelContent(null); setPreviewOpen(false); }}
                    onRefresh={() => {
                      if (panelContent.type === 'todo-detail') {
                        handleAction('refresh-todo', panelContent.todo)
                      }
                    }}
                    onOpenBasecamp={() => {
                      if (panelContent.type === 'todo-detail') {
                        handleAction('open-basecamp', panelContent.todo)
                      } else {
                        window.open(`https://app.basecamp.com/todo/${panelContent.todo.id}`, '_blank')
                      }
                    }}
                  />
                ) : (
                  <div className="flex h-full items-center justify-center p-4 text-center text-xs text-muted-foreground">
                    Select a todo to preview
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </main>
      </div>

      {contextMenu ? (
        <EntityContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          data-testid={testIds.basecampTodosTest.contextMenu}
          onClose={() => setContextMenu(null)}
          entries={menuEntriesForTodo(contextMenu.todo, (action, todo) => {
            handleAction(action, todo)
            setContextMenu(null)
          })}
        />
      ) : null}
    </div>
    </TooltipProvider>
  );
}
